import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';

/**
 * Custom Booking Widget Component
 * Features date picker and guest selector with proper click handling
 */
const BookingWidget = ({ variant = 'hero' }) => {
  const { language } = useLanguage();
  
  // Translations
  const translations = {
    it: {
      arrival: 'Arrivo',
      departure: 'Partenza',
      guests: 'Ospiti',
      rooms: 'Camere',
      room: 'Camera',
      checkAvailability: 'Verifica Disponibilità',
      done: 'Fatto',
      adult: 'Adulto',
      adults: 'Adulti'
    },
    en: {
      arrival: 'Arrival',
      departure: 'Departure',
      guests: 'Guests',
      rooms: 'Rooms',
      room: 'Room',
      checkAvailability: 'Check Availability',
      done: 'Done',
      adult: 'Adult',
      adults: 'Adults'
    }
  };
  
  const t = translations[language] || translations.en;
  const [arrivalDate, setArrivalDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  
  const [departureDate, setDepartureDate] = useState(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  });
  
  const [guests, setGuests] = useState(2);
  const [rooms, setRooms] = useState(1);
  const [showGuestPopup, setShowGuestPopup] = useState(false);
  
  const arrivalRef = useRef(null);
  const departureRef = useRef(null);
  const guestPopupRef = useRef(null);

  // Close guest popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (guestPopupRef.current && !guestPopupRef.current.contains(e.target)) {
        setShowGuestPopup(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Format date for display
  const formatDateDisplay = (dateStr) => {
    const date = new Date(dateStr);
    const day = date.getDate();
    const month = date.toLocaleString(language === 'it' ? 'it-IT' : 'en-US', { month: 'short' });
    const weekday = date.toLocaleString(language === 'it' ? 'it-IT' : 'en-US', { weekday: 'short' });
    return { day, month, weekday };
  };

  const arrivalFormatted = formatDateDisplay(arrivalDate);
  const departureFormatted = formatDateDisplay(departureDate);

  // Handle date change
  const handleArrivalChange = (e) => {
    const newArrival = e.target.value;
    setArrivalDate(newArrival);
    
    // Ensure departure is after arrival
    if (new Date(newArrival) >= new Date(departureDate)) {
      const nextDay = new Date(newArrival);
      nextDay.setDate(nextDay.getDate() + 1);
      setDepartureDate(nextDay.toISOString().split('T')[0]);
    }
  };

  const handleDepartureChange = (e) => {
    const newDeparture = e.target.value;
    if (new Date(newDeparture) > new Date(arrivalDate)) {
      setDepartureDate(newDeparture);
    }
  };

  // Check availability - redirect to Kross with exact parameters
  const checkAvailability = () => {
    const baseUrl = 'https://royalroomsbbnapoli.kross.travel/book/step1';
    
    // Build guests_rooms parameter
    // Format: "guests,children;" repeated for each room
    // e.g., 1 room with 2 guests = "2,0;"
    // e.g., 2 rooms with 2 guests each = "2,0;2,0;"
    const guestsPerRoom = Math.ceil(guests / rooms);
    let guestsRooms = '';
    let remainingGuests = guests;
    
    for (let i = 0; i < rooms; i++) {
      const guestsInThisRoom = Math.min(guestsPerRoom, remainingGuests);
      remainingGuests -= guestsInThisRoom;
      guestsRooms += `${guestsInThisRoom},0;`;
    }
    
    const params = new URLSearchParams({
      from: arrivalDate,
      to: departureDate,
      guests_rooms: guestsRooms,
      detailrt: 0
    });
    
    window.open(`${baseUrl}?${params.toString()}`, '_blank');
  };

  const isHero = variant === 'hero';
  const isSticky = variant === 'sticky';

  return (
    <div className={`booking-widget ${isHero ? 'booking-widget-hero' : 'booking-widget-sticky'}`}>
      <div className={`flex flex-wrap items-stretch gap-2 ${isHero ? 'bg-white/95 backdrop-blur-sm rounded-xl p-3 shadow-2xl' : 'bg-gray-100 rounded-lg p-2'}`}>
        
        {/* Arrival Date */}
        <div 
          className={`relative flex-1 min-w-[100px] ${isHero ? 'bg-white' : 'bg-white'} rounded-lg p-2 sm:p-3 cursor-pointer hover:bg-gray-50 transition-colors border border-gray-200`}
          onClick={() => arrivalRef.current?.showPicker()}
        >
          <input
            ref={arrivalRef}
            type="date"
            value={arrivalDate}
            min={new Date().toISOString().split('T')[0]}
            onChange={handleArrivalChange}
            className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
          />
          <div className="text-center pointer-events-none">
            <div className={`text-xs uppercase tracking-wide ${isHero ? 'text-primary-bronze' : 'text-gray-500'} font-semibold`}>{t.arrival}</div>
            <div className={`text-2xl sm:text-3xl font-bold ${isHero ? 'text-primary-navy' : 'text-primary-bronze'}`}>{arrivalFormatted.day}</div>
            <div className="text-xs text-gray-600">{arrivalFormatted.month}</div>
          </div>
        </div>

        {/* Departure Date */}
        <div 
          className={`relative flex-1 min-w-[100px] ${isHero ? 'bg-white' : 'bg-white'} rounded-lg p-2 sm:p-3 cursor-pointer hover:bg-gray-50 transition-colors border border-gray-200`}
          onClick={() => departureRef.current?.showPicker()}
        >
          <input
            ref={departureRef}
            type="date"
            value={departureDate}
            min={arrivalDate}
            onChange={handleDepartureChange}
            className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
          />
          <div className="text-center pointer-events-none">
            <div className={`text-xs uppercase tracking-wide ${isHero ? 'text-primary-bronze' : 'text-gray-500'} font-semibold`}>{t.departure}</div>
            <div className={`text-2xl sm:text-3xl font-bold ${isHero ? 'text-primary-navy' : 'text-primary-bronze'}`}>{departureFormatted.day}</div>
            <div className="text-xs text-gray-600">{departureFormatted.month}</div>
          </div>
        </div>

        {/* Guests & Rooms */}
        <div 
          ref={guestPopupRef}
          className={`relative flex-1 min-w-[100px] ${isHero ? 'bg-white' : 'bg-white'} rounded-lg p-2 sm:p-3 cursor-pointer hover:bg-gray-50 transition-colors border border-gray-200`}
          onClick={() => setShowGuestPopup(!showGuestPopup)}
        >
          <div className="text-center">
            <div className={`text-xs uppercase tracking-wide ${isHero ? 'text-primary-bronze' : 'text-gray-500'} font-semibold`}>{t.guests}</div>
            <div className={`text-2xl sm:text-3xl font-bold ${isHero ? 'text-primary-navy' : 'text-primary-bronze'}`}>{guests}</div>
            <div className="text-xs text-gray-600">{rooms} {rooms > 1 ? t.rooms : t.room}</div>
          </div>

          {/* Guest Popup */}
          {showGuestPopup && (
            <div 
              className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-2xl border border-gray-200 p-4 z-[9999] min-w-[200px]"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Guests */}
              <div className="flex items-center justify-between mb-4">
                <span className="text-gray-700 font-medium">{t.guests}</span>
                <div className="flex items-center gap-3">
                  <button 
                    onClick={() => setGuests(Math.max(1, guests - 1))}
                    className="w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center text-lg font-bold transition-colors"
                  >
                    -
                  </button>
                  <span className="w-8 text-center font-bold text-lg">{guests}</span>
                  <button 
                    onClick={() => setGuests(Math.min(10, guests + 1))}
                    className="w-8 h-8 rounded-full bg-primary-bronze text-white hover:bg-[#7a5230] flex items-center justify-center text-lg font-bold transition-colors"
                  >
                    +
                  </button>
                </div>
              </div>

              {/* Rooms */}
              <div className="flex items-center justify-between">
                <span className="text-gray-700 font-medium">{t.rooms}</span>
                <div className="flex items-center gap-3">
                  <button 
                    onClick={() => setRooms(Math.max(1, rooms - 1))}
                    className="w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center text-lg font-bold transition-colors"
                  >
                    -
                  </button>
                  <span className="w-8 text-center font-bold text-lg">{rooms}</span>
                  <button 
                    onClick={() => setRooms(Math.min(5, rooms + 1))}
                    className="w-8 h-8 rounded-full bg-primary-bronze text-white hover:bg-[#7a5230] flex items-center justify-center text-lg font-bold transition-colors"
                  >
                    +
                  </button>
                </div>
              </div>

              {/* Done Button */}
              <button 
                onClick={() => setShowGuestPopup(false)}
                className="w-full mt-4 bg-primary-bronze text-white py-2 rounded-lg font-semibold hover:bg-[#7a5230] transition-colors"
              >
                {t.done}
              </button>
            </div>
          )}
        </div>

        {/* Check Availability Button */}
        <button 
          onClick={checkAvailability}
          className={`flex-shrink-0 ${isHero ? 'bg-primary-bronze hover:bg-[#7a5230] text-white px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg' : 'bg-primary-bronze hover:bg-[#7a5230] text-white px-4 sm:px-6 py-2 sm:py-3 text-sm sm:text-base'} rounded-lg font-bold transition-all hover:shadow-lg`}
        >
          {t.checkAvailability}
        </button>
      </div>
    </div>
  );
};

export default BookingWidget;
